// Carteles de cancha: PDF con una hoja A4 por cada cancha que se usa en la
// jornada. Cada hoja es el logo arriba y abajo el número de cancha lo más grande
// que entre, para colgarlo y que se lea de lejos.
//
// jspdf se carga con import() dinámico para que no entre en el bundle principal
// de la PWA: solo se baja cuando se toca el botón.

// El logo de la app (el mismo del ícono de la PWA). Es un PNG cuadrado con aire
// alrededor: el arte ocupa el 84.8% del ancho y va del 23.4% al 76.2% del alto.
// Esas proporciones son las que dejan el arte donde va en la hoja.
const LOGO = process.env.PUBLIC_URL + "/images/icon_512x512.png";

// jsPDF mide las fuentes en puntos y la hoja en milímetros.
const MM_POR_PUNTO = 0.3528;
// Alto de un dígito de Helvetica respecto del cuerpo de letra (cap height). Con
// esto el número se ubica por su alto real y no por la caja de la fuente, que
// lleva aire arriba y abajo.
const ALTO_DE_DIGITO = 0.717;

// Caja del logo y franja del número, en milímetros sobre una hoja A4.
const LOGO_ANCHO = 78;
const LOGO_TOP = 14;
const NUMERO_DESDE = 101;
const NUMERO_HASTA = 227;
const MARGEN = 14;

const limpiarNombreArchivo = (texto) => texto.replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim();

// El logo se baja y se pasa como data URI. Si en vez de eso se le da a jsPDF un
// <img> con una URL, lo resuelve con un XHR sincrónico que traba la pantalla.
const cargarLogo = async () => {
  const respuesta = await fetch(LOGO);
  const blob = await respuesta.blob();
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result);
    lector.onerror = reject;
    lector.readAsDataURL(blob);
  });
};

// canchas: los números de cancha de la jornada, ya ordenados (["1", "2", "3"]).
// torneoNombre / torneoAno / numero / dia solo se usan para el nombre del archivo.
export async function generarCartelesCanchas({ canchas = [], torneoNombre, torneoAno, numero, dia }) {
  if (canchas.length === 0) return;

  const [{ jsPDF }, logo] = await Promise.all([
    import("jspdf"),
    // Si el logo no carga (sin conexión y sin cachear), el cartel sale igual con
    // el número solo.
    cargarLogo().catch(() => null),
  ]);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const anchoPagina = doc.internal.pageSize.getWidth();
  const anchoUtil = anchoPagina - MARGEN * 2;

  canchas.forEach((cancha, indice) => {
    if (indice > 0) doc.addPage();

    if (logo) doc.addImage(logo, "PNG", (anchoPagina - LOGO_ANCHO) / 2, LOGO_TOP, LOGO_ANCHO, LOGO_ANCHO);

    const texto = String(cancha);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);

    // El número entra por alto en la franja; si es de dos dígitos y se pasa de
    // ancho, se achica hasta entrar y queda centrado en la misma franja.
    let alto = NUMERO_HASTA - NUMERO_DESDE;
    let cuerpo = alto / ALTO_DE_DIGITO / MM_POR_PUNTO;
    doc.setFontSize(cuerpo);
    const ancho = doc.getTextWidth(texto);
    if (ancho > anchoUtil) {
      cuerpo *= anchoUtil / ancho;
      alto = cuerpo * MM_POR_PUNTO * ALTO_DE_DIGITO;
      doc.setFontSize(cuerpo);
    }

    // doc.text apoya el texto en la línea de base, que es el pie de los dígitos.
    const base = (NUMERO_DESDE + NUMERO_HASTA) / 2 + alto / 2;
    doc.text(texto, anchoPagina / 2, base, { align: "center" });
  });

  const nombreArchivo = limpiarNombreArchivo(
    [
      "Carteles Canchas",
      [torneoNombre || "", torneoAno].filter(Boolean).join(" "),
      numero ? "Fecha " + numero : "",
      dia || "",
    ]
      .filter(Boolean)
      .join(" - ")
  );

  doc.save(nombreArchivo + ".pdf");
}
